'use client';

import React, { useState } from 'react';
import { SearchParams } from '@/constants/listing-search-keys';
import { DefaultGuests } from '@/context/listing-search-provider';
import { createInquiry } from '@/services/salesforce/endpoints/create-inquiry';
import {
  parseGuestCountsFromListingQuery,
  totalGuestsFromCounts,
} from '@/utils/guest-search-params';
import { Listing } from '@/utils/platform-api/__generated__/graphql';
import { parseSafeDate } from '@/utils/parse-safe-date';
import { useBreakpoint } from '@/utils/useBreakpoint';
import { validateEmail } from '@/utils/validate-email';
import clsx from 'clsx';
import {
  isValidPhoneNumber,
  ParseError,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { DateRange } from 'react-day-picker';
import { ClientBrandConfig } from 'types/brand-config';

import Button from '@/components/ui/button';
import TextArea from '@/components/ui/text-area';
import Text from '@/components/ui/text-input';
import DateSearch from '@/components/search/date-search';
import GuestSelection from '@/components/search/guest-selection';
import { useTurnstile } from '@/components/turnstile-provider';

const InquiryForm: React.FC<{
  listing: Listing;
  searchParams?: {
    arrive?: string;
    depart?: string;
    guests?: string;
    adults?: string;
    children?: string;
    flex_weeks?: string;
    flex_days?: string;
  };
  brand: ClientBrandConfig;
}> = ({ listing, searchParams, brand }) => {
  const defaultFormState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    comments: '',
    guests: searchParams
      ? totalGuestsFromCounts(parseGuestCountsFromListingQuery(searchParams))
      : DefaultGuests,
    pets: '0',
    selectedDates: (() => {
      const from = parseSafeDate(searchParams?.[SearchParams.ARRIVE]);
      const to = parseSafeDate(searchParams?.[SearchParams.DEPART]);
      return from && to
        ? ({ from, to } as DateRange)
        : { from: undefined, to: undefined };
    })(),
  };
  const [formErrors, setFormErrors] = useState<
    Record<keyof typeof defaultFormState, string | null>
  >(
    Object.assign(
      {},
      ...Object.keys(defaultFormState).map((key) => ({ [key]: null }))
    )
  );
  const [formState, setFormState] = useState(defaultFormState);
  const [formValid, setFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const breakpoint = useBreakpoint();
  const isDesktopView =
    breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
  const { hasToken, consumeToken, isVerifying } = useTurnstile();

  const hasErrors = Object.values(formErrors).some((error) => !!error);
  const missingDates =
    !formState.selectedDates ||
    !formState.selectedDates.from ||
    !formState.selectedDates.to;
  // Check if Turnstile verification is in progress or incomplete
  const turnstileIncomplete = !hasToken() || isVerifying;

  const submitDisabled =
    !formValid ||
    hasErrors ||
    missingDates ||
    submitting ||
    turnstileIncomplete;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitSuccessful(false);
    if (!e.currentTarget.checkValidity()) {
      return;
    }
    if (
      !formState.selectedDates ||
      !formState.selectedDates.from ||
      !formState.selectedDates.to
    ) {
      setFormErrors({
        ...formErrors,
        selectedDates: 'Please select travel dates.',
      });
      return;
    }
    setSubmitting(true);
    const formPayload: InquiryParamsClient = {
      first_name: formState.firstName,
      last_name: formState.lastName,
      email: formState.email,
      phone: formState.phone,
      adults: formState.guests ? Number(formState.guests) : 0,
      comments: formState.comments,
      check_in_date: formState.selectedDates.from,
      check_out_date: formState.selectedDates.to,
      // ...(typeof listing.max_pets === 'number' && { pets: formState.pets ? Number(formState.pets) : 0 })
    };
    if (!hasToken()) {
      console.warn('Please complete the security check');
      setSubmitting(false);
      return;
    }

    const token = consumeToken();

    if (!token) {
      console.warn('Failed to get security token');
      setSubmitting(false);
      return;
    }

    try {
      const response = await createInquiry(formPayload, listing.id, token);
      if (response && !response?.error) {
        setFormState(defaultFormState);
        setFormErrors(
          Object.assign(
            {},
            ...Object.keys(defaultFormState).map((key) => ({ [key]: null }))
          )
        );
        setSubmitSuccessful(true);
      }
    } catch (e: any) {
      alert(
        `There was an error submitting your form. Please try again. ${e.message}`
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="block">
      <h4 className="section-header mb-[1rem]">Request Information</h4>
      <form
        onChange={(e) => {
          if (submitting) {
            return;
          }
          setFormValid(e.currentTarget.checkValidity());
          setSubmitSuccessful(false);
        }}
        onSubmit={onSubmit}
        id="inquiry-form"
        className="flex flex-col gap-[1.88rem] rounded-xl bg-neutral-light p-[1.88rem]"
      >
        <div className="grid grid-cols-1 gap-[1.88rem] sm:grid-cols-2">
          <div className="flex flex-col">
            <Text
              id="inquiry-first-name"
              radius="square"
              required
              disabled={submitting}
              hasError={!!formErrors.firstName}
              value={formState.firstName}
              onChange={(e) => {
                setFormErrors({ ...formErrors, firstName: null });
                setFormState({
                  ...formState,
                  firstName: e.currentTarget.value,
                });
              }}
              style="white"
              label="First"
              name="first-name"
              placeholder="First Name"
              className="col-span-1 border"
            />
            {formErrors.firstName && (
              <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
                {formErrors.firstName}
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <Text
              id="inquiry-last-name"
              radius="square"
              required
              disabled={submitting}
              hasError={!!formErrors.lastName}
              value={formState.lastName}
              onChange={(e) => {
                setFormErrors({ ...formErrors, lastName: null });
                setFormState({ ...formState, lastName: e.currentTarget.value });
              }}
              style="white"
              label="Last"
              name="last-name"
              placeholder="Last Name"
              className="col-span-1 border"
            />
            {formErrors.lastName && (
              <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
                {formErrors.lastName}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <Text
            id="inquiry-email"
            required
            radius="square"
            disabled={submitting}
            hasError={!!formErrors.email}
            value={formState.email}
            onChange={(e) => {
              setFormState({ ...formState, email: e.currentTarget.value });
              if (validateEmail(e.currentTarget.value)) {
                setFormErrors({ ...formErrors, email: null });
                return;
              }
              setFormErrors({
                ...formErrors,
                email: 'Please provide a valid email.',
              });
            }}
            style="white"
            label="Email"
            name="email"
            placeholder="Email"
            type="email"
            className="col-span-1 border"
          />
          {formErrors.email && (
            <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
              {formErrors.email}
            </span>
          )}
        </div>
        <div className="flex flex-col">
          <Text
            id="inquiry-phone"
            radius="square"
            hasError={!!formErrors.phone}
            value={formState.phone}
            disabled={submitting}
            onChange={(e) => {
              setFormState({ ...formState, phone: e.currentTarget.value });
              try {
                if (e.currentTarget.value) {
                  // Try to parse the phone number with the country code
                  const phoneVal = e.currentTarget.value
                    .replace('+', '')
                    .trim();
                  const parsedNumber = parsePhoneNumberWithError(
                    '+' + phoneVal,
                    { extract: true }
                  );
                  if (!isValidPhoneNumber(parsedNumber.formatInternational())) {
                    throw new Error('Please provide a different phone number.');
                  }
                }
                setFormErrors({ ...formErrors, phone: null });
              } catch (error) {
                try {
                  if (e.currentTarget.value) {
                    // Try to parse the phone number with the US country code
                    const phoneVal = e.currentTarget.value
                      .replace('+', '')
                      .trim();
                    const parsedNumber = parsePhoneNumberWithError(
                      '+1' + phoneVal,
                      { extract: true }
                    );
                    if (
                      !isValidPhoneNumber(parsedNumber.formatInternational())
                    ) {
                      throw new Error(
                        'Please provide a different phone number.'
                      );
                    }
                  }
                  setFormErrors({ ...formErrors, phone: null });
                } catch (error) {
                  if (error instanceof ParseError) {
                    // If we ever need to be more specific about the error
                    switch (error.message) {
                      case 'TOO_SHORT':
                        setFormErrors({
                          ...formErrors,
                          phone: 'Invalid phone number (Too short).',
                        });
                        return;
                      case 'TOO_LONG':
                        setFormErrors({
                          ...formErrors,
                          phone: 'Invalid phone number (Too long).',
                        });
                        return;
                      case 'INVALID_COUNTRY':
                        setFormErrors({
                          ...formErrors,
                          phone: 'Invalid country code.',
                        });
                        return;
                      default:
                        setFormErrors({
                          ...formErrors,
                          phone: 'Invalid phone number',
                        });
                        return;
                    }
                  }
                  setFormErrors({
                    ...formErrors,
                    phone: 'Invalid phone number.',
                  });
                  return;
                }
              }
            }}
            inputMode="numeric"
            style="white"
            label="Phone"
            name="phone"
            type="tel"
            placeholder="Phone Number"
            className="col-span-1 border"
          />
          {formErrors.phone && (
            <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
              {formErrors.phone}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-[1.88rem] sm:grid-cols-2">
          <div className="flex flex-col">
            <div className="border">
              <DateSearch
                brand={brand}
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    selectedDates: value || { from: undefined, to: undefined },
                  }))
                }
                selectedDates={
                  {
                    from: formState.selectedDates.from,
                    to: formState.selectedDates.to,
                  } as DateRange
                }
                radius="square"
                required
                hasError={!!formErrors.selectedDates}
                hideWeekdays={false}
                hideFlex
                numberOfMonths={isDesktopView ? 2 : 12}
                direction={isDesktopView ? 'horizontal' : 'vertical'}
                hideNavigation={!isDesktopView}
                label="Travel Dates"
                id="inqury-travel-dates"
                placeholder="Travel Dates"
                name="dates"
                applySearchParams={() => null}
                clearDateParams={() => {
                  setFormState({
                    ...formState,
                    selectedDates: { from: undefined, to: undefined },
                  });
                }}
                setDateParam={() => null}
              />
            </div>
            {formErrors.selectedDates && (
              <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
                {formErrors.selectedDates}
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <div className="border">
              <GuestSelection
                id="inquiry-guests"
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    guests: value || DefaultGuests,
                  }))
                }
                value={formState.guests ? formState.guests : DefaultGuests}
                radius="square"
                required
                hasError={!!formErrors.guests}
                pos="top"
                className="!w-full"
                style="white"
                classNames={{
                  pannel:
                    'bg-white w-full max-w-[14.5rem] shadow-lg mt-[3.75rem] rounded-xl transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in',
                }}
              />
            </div>
            {formErrors.guests && (
              <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
                {formErrors.guests}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <TextArea
            required
            id="inquiry-comments"
            disabled={submitting}
            hasError={!!formErrors.comments}
            value={formState.comments}
            onChange={(e) => {
              setFormErrors({ ...formErrors, comments: null });
              setFormState({ ...formState, comments: e.currentTarget.value });
            }}
            style="white"
            radius="square"
            label="Comments"
            name="comments"
            placeholder="Comments"
          />
          {formErrors.comments && (
            <span className="-mb-[1.25rem] mt-[0.25rem] text-[0.75rem] text-error-dark">
              {formErrors.comments}
            </span>
          )}
        </div>

        <Button
          aria-label="Submit inquiry form"
          disabled={submitDisabled}
          disabledCTA
          type="submit"
          style="secondary"
          className="relative self-start"
        >
          {submitting && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="2em"
                height="2em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="rgb(var(--secondary-main-rgb))"
                  strokeDasharray="16"
                  strokeDashoffset="16"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.95"
                  d="M12 3c4.97 0 9 4.03 9 9"
                >
                  <animate
                    fill="freeze"
                    attributeName="stroke-dashoffset"
                    dur="0.2s"
                    values="16;0"
                  />
                  <animateTransform
                    attributeName="transform"
                    dur="1.5s"
                    repeatCount="indefinite"
                    type="rotate"
                    values="0 12 12;360 12 12"
                  />
                </path>
              </svg>
            </div>
          )}
          <span className={clsx('text-inherit', submitting && 'invisible')}>
            Send Message
          </span>
        </Button>
        {submitSuccessful && (
          <span className="ut-sm -mt-[.5rem] text-success-dark">
            Message sent successfully!
          </span>
        )}
      </form>
    </div>
  );
};

export default InquiryForm;
